/*
 * Elix library: smart layer (Phase 3). Plain JavaScript, no dependencies.
 *
 * Every page works without this file; it adds:
 * - Region / radius builds on cocktail pages, per ingredient for mixed builds
 * - Search and filters on the cocktail, spirit and distillery lists
 * - "My bar": bottles you own (this device only) and "What can I make"
 *
 * Bottles on cocktail pages are already matched in Liquid
 * (snippets/elix-match), so filters there only show / hide cards. "What can
 * I make" needs every recipe at once, so it reads the JSON index from
 * templates/search.elix-index.liquid, using the same rule as elix-match.
 */
(function () {
	'use strict';

	if (window.ElixLibrary) return;

	var INDEX_URL = '/search?view=elix-index';
	var INDEX_CACHE_KEY = 'elix-index';
	var INDEX_CACHE_MS = 10 * 60 * 1000;
	var BAR_KEY = 'elix-bar';
	var SAVED_KEY = 'elix-saved';
	var LOCATION_KEY = 'elix-location';

	/* ---------- Storage (private browsing or blocked storage must not break pages) ---------- */

	function store(name) {
		try {
			return window[name];
		} catch (e) {
			return null;
		}
	}

	function readJSON(storeName, key, fallback) {
		try {
			var raw = store(storeName) && store(storeName).getItem(key);
			return raw ? JSON.parse(raw) : fallback;
		} catch (e) {
			return fallback;
		}
	}

	function writeJSON(storeName, key, value) {
		try {
			store(storeName).setItem(key, JSON.stringify(value));
		} catch (e) {
			/* storage unavailable: the feature just won't persist */
		}
	}

	/* ---------- Data index ---------- */

	var indexPromise = null;

	function loadIndex() {
		if (indexPromise) return indexPromise;

		var cached = readJSON('sessionStorage', INDEX_CACHE_KEY, null);
		if (cached && cached.at && Date.now() - cached.at < INDEX_CACHE_MS) {
			indexPromise = Promise.resolve(cached.data);
			return indexPromise;
		}

		indexPromise = fetch(INDEX_URL, { credentials: 'same-origin' })
			.then(function (response) {
				if (!response.ok) throw new Error('Elix index: HTTP ' + response.status);
				return response.json();
			})
			.then(function (data) {
				writeJSON('sessionStorage', INDEX_CACHE_KEY, { at: Date.now(), data: data });
				return data;
			})
			.catch(function (error) {
				indexPromise = null;
				throw error;
			});
		return indexPromise;
	}

	/* ---------- Matching (mirrors snippets/elix-match.liquid) ---------- */

	// Returns the match %, or -1 when the spirit can't fill the slot at all.
	// A ticked spirit type in My bar ("I have gin", no bottle named) counts as
	// just good enough for any slot of that type.
	function score(spirit, slot) {
		if (spirit.generic) return spirit.type === slot.type ? slot.min : -1;
		if (spirit.type !== slot.type || !slot.need.length) return -1;
		if (slot.cat && spirit.cat !== slot.cat) return -1;
		var hit = slot.need.filter(function (tag) { return spirit.tags.indexOf(tag) > -1; }).length;
		var bad = slot.avoid.filter(function (tag) { return spirit.tags.indexOf(tag) > -1; }).length;
		return Math.max(0, Math.round((hit / slot.need.length) * 100) - bad * 25);
	}

	// Which owned bottle covers each slot; missing lists the required slots nothing covers.
	function canMake(cocktail, spirits) {
		var picks = [];
		var missing = [];
		cocktail.slots.forEach(function (slot) {
			var best = null;
			spirits.forEach(function (spirit) {
				var pct = score(spirit, slot);
				if (pct >= slot.min && (!best || pct > best.pct)) best = { spirit: spirit, pct: pct };
			});
			if (best) picks.push({ slot: slot, spirit: best.spirit, pct: best.pct });
			else if (!slot.opt) missing.push(slot.n);
		});
		return { ok: missing.length === 0 && cocktail.slots.length > 0, picks: picks, missing: missing };
	}

	/* ---------- Places ---------- */

	// Haversine distance in km
	function km(lat1, lng1, lat2, lng2) {
		var R = 6371;
		var rad = function (d) { return (d * Math.PI) / 180; };
		var a =
			Math.pow(Math.sin(rad(lat2 - lat1) / 2), 2) +
			Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.pow(Math.sin(rad(lng2 - lng1) / 2), 2);
		return 2 * R * Math.asin(Math.sqrt(a));
	}

	function cardDistance(card, near) {
		var lat = parseFloat(card.dataset.lat);
		var lng = parseFloat(card.dataset.lng);
		if (!near || isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return null;
		return km(near.lat, near.lng, lat, lng);
	}

	// scope: { kind: 'any' } | { kind: 'region', region } | { kind: 'near', near, radius }
	function cardPasses(card, scope) {
		if (!scope || scope.kind === 'any') return true;
		if (scope.kind === 'region') {
			return (' ' + (card.dataset.regionPath || '') + ' ').indexOf(' ' + scope.region + ' ') > -1;
		}
		if (scope.kind === 'near') {
			if (!scope.near) return true;
			var distance = cardDistance(card, scope.near);
			return distance !== null && distance <= scope.radius;
		}
		return true;
	}

	function showDistance(card, scope) {
		var el = card.querySelector('[data-elix-distance]');
		if (!el) return;
		var distance = scope && scope.kind === 'near' ? cardDistance(card, scope.near) : null;
		el.hidden = distance === null;
		el.textContent = distance === null ? '' : ' · ' + Math.round(distance) + ' km away';
	}

	/* ---------- Location controls (snippets/elix-location-controls) ---------- */

	function getLocation() {
		return readJSON('sessionStorage', LOCATION_KEY, null);
	}

	function setLocation(location) {
		writeJSON('sessionStorage', LOCATION_KEY, location);
		document.dispatchEvent(new CustomEvent('elix:location', { detail: location }));
	}

	function geocodePostcode(postcode, token) {
		var url =
			'https://api.mapbox.com/search/geocode/v6/forward?country=au&types=postcode,place&limit=1&q=' +
			encodeURIComponent(postcode) +
			'&access_token=' +
			encodeURIComponent(token);
		return fetch(url)
			.then(function (response) {
				if (!response.ok) throw new Error('Geocoding failed');
				return response.json();
			})
			.then(function (data) {
				var feature = data.features && data.features[0];
				if (!feature) throw new Error('Postcode not found');
				return {
					lat: feature.geometry.coordinates[1],
					lng: feature.geometry.coordinates[0],
					label: postcode
				};
			});
	}

	function initLocationControls(root) {
		var status = root.querySelector('[data-elix-location-status]');
		var locateButton = root.querySelector('[data-elix-locate]');
		var postcodeForm = root.querySelector('[data-elix-postcode]');
		var radius = root.querySelector('[data-elix-radius]');
		var radiusOut = root.querySelector('[data-elix-radius-out]');

		function say(text) {
			if (status) status.textContent = text;
		}

		function showCurrent() {
			var location = getLocation();
			say(location ? 'Searching from ' + location.label + '.' : root.dataset.promptText || '');
		}

		if (radius && radiusOut) {
			radiusOut.textContent = radius.value;
			radius.addEventListener('input', function () {
				radiusOut.textContent = radius.value;
			});
		}

		if (locateButton) {
			if (!navigator.geolocation) {
				locateButton.hidden = true;
			} else {
				locateButton.addEventListener('click', function () {
					say('Finding you…');
					navigator.geolocation.getCurrentPosition(
						function (position) {
							setLocation({ lat: position.coords.latitude, lng: position.coords.longitude, label: 'your location' });
						},
						function () {
							say("We couldn't get your location. Try a postcode instead.");
						},
						{ maximumAge: 600000, timeout: 10000 }
					);
				});
			}
		}

		if (postcodeForm) {
			postcodeForm.addEventListener('submit', function (event) {
				event.preventDefault();
				var input = postcodeForm.querySelector('input');
				var postcode = (input.value || '').trim();
				if (!/^\d{4}$/.test(postcode)) {
					say('Enter a 4-digit postcode.');
					return;
				}
				say('Looking up ' + postcode + '…');
				geocodePostcode(postcode, root.dataset.mapboxToken)
					.then(setLocation)
					.catch(function () {
						say("We couldn't find " + postcode + '.');
					});
			});
		}

		document.addEventListener('elix:location', showCurrent);
		showCurrent();
	}

	function readScope(select, radiusInput) {
		var value = select ? select.value : '';
		if (!value || value === 'any') return { kind: 'any' };
		if (value === 'near') {
			return { kind: 'near', near: getLocation(), radius: radiusInput ? Number(radiusInput.value) : 50 };
		}
		var option = select.options[select.selectedIndex];
		return { kind: 'region', region: value, demonym: option.dataset.demonym || option.textContent.trim() };
	}

	/* ---------- My bar ---------- */

	function getBar() {
		var bar = readJSON('localStorage', BAR_KEY, []);
		return Array.isArray(bar) ? bar : [];
	}

	function setBar(bar) {
		writeJSON('localStorage', BAR_KEY, bar);
		document.dispatchEvent(new CustomEvent('elix:bar', { detail: bar }));
	}

	function toggleInBar(handle, on) {
		var bar = getBar().filter(function (h) { return h !== handle; });
		if (on) bar.push(handle);
		setBar(bar);
	}

	// "In your bar" tag on every spirit card for a bottle you own
	function markOwnedCards() {
		var bar = getBar();
		document.querySelectorAll('.elix-card[data-spirit]').forEach(function (card) {
			var owned = bar.indexOf(card.dataset.spirit) > -1;
			card.classList.toggle('is-in-bar', owned);
			var tag = card.querySelector('[data-elix-owned]');
			if (tag) tag.hidden = !owned;
		});
	}

	// Spirit page "Add to my bar" button
	function initBarToggle(button) {
		var handle = button.dataset.elixBarToggle;
		function render() {
			var owned = getBar().indexOf(handle) > -1;
			button.setAttribute('aria-pressed', owned ? 'true' : 'false');
			button.textContent = owned ? button.dataset.removeText : button.dataset.addText;
		}
		button.hidden = false;
		button.addEventListener('click', function () {
			toggleInBar(handle, button.getAttribute('aria-pressed') !== 'true');
		});
		document.addEventListener('elix:bar', render);
		render();
	}

	/* ---------- Saved cocktails (free account feature, kept on this device) ---------- */

	function getSaved() {
		var saved = readJSON('localStorage', SAVED_KEY, []);
		return Array.isArray(saved) ? saved : [];
	}

	function setSaved(saved) {
		writeJSON('localStorage', SAVED_KEY, saved);
		document.dispatchEvent(new CustomEvent('elix:saved', { detail: saved }));
	}

	// Cocktail page "Save cocktail" button (only rendered for signed-in customers)
	function initSaveToggle(button) {
		var handle = button.dataset.elixSave;
		function render() {
			var saved = getSaved().indexOf(handle) > -1;
			button.setAttribute('aria-pressed', saved ? 'true' : 'false');
			button.textContent = saved ? button.dataset.removeText : button.dataset.addText;
		}
		button.hidden = false;
		button.addEventListener('click', function () {
			var saved = getSaved().filter(function (h) { return h !== handle; });
			if (button.getAttribute('aria-pressed') !== 'true') saved.push(handle);
			setSaved(saved);
		});
		document.addEventListener('elix:saved', render);
		render();
	}

	// Members' lounge: every cocktail card is rendered hidden; show the saved ones
	function initSavedList(root) {
		var cards = root.querySelectorAll('.elix-card[data-cocktail]');
		var empty = root.querySelector('[data-elix-saved-empty]');
		function render() {
			var saved = getSaved();
			var shown = 0;
			cards.forEach(function (card) {
				var on = saved.indexOf(card.dataset.cocktail) > -1;
				card.hidden = !on;
				if (on) shown += 1;
			});
			if (empty) empty.hidden = shown > 0;
		}
		document.addEventListener('elix:saved', render);
		render();
	}

	// Member card: a ticking clock, so bar staff can tell it's live and not a screenshot
	function initMemberCard(card) {
		var clock = card.querySelector('[data-elix-clock]');
		if (!clock) return;
		function tick() {
			clock.textContent = new Date().toLocaleString('en-AU', {
				weekday: 'short', day: 'numeric', month: 'short',
				hour: 'numeric', minute: '2-digit', second: '2-digit'
			});
		}
		tick();
		setInterval(tick, 1000);
	}

	/* ---------- Map of distilleries and bars (sections/elix-map) ---------- */

	var MAPBOX_VERSION = '3.6.0';
	var mapboxPromise = null;

	// Mapbox GL is only fetched for pages that show a map, and only once
	function loadMapbox() {
		if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
		if (mapboxPromise) return mapboxPromise;
		mapboxPromise = new Promise(function (resolve, reject) {
			var base = 'https://api.mapbox.com/mapbox-gl-js/v' + MAPBOX_VERSION + '/mapbox-gl';
			var css = document.createElement('link');
			css.rel = 'stylesheet';
			css.href = base + '.css';
			document.head.appendChild(css);
			var script = document.createElement('script');
			script.src = base + '.js';
			script.onload = function () { resolve(window.mapboxgl); };
			script.onerror = reject;
			document.head.appendChild(script);
		});
		return mapboxPromise;
	}

	function escapeHTML(text) {
		return String(text == null ? '' : text).replace(/[&<>"']/g, function (c) {
			return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
		});
	}

	function popupHTML(place) {
		var tags = (place.f || []).filter(Boolean).map(function (label) {
			return '<li>' + escapeHTML(label) + '</li>';
		}).join('');
		return '<div class="elix-mappop">' +
			'<p class="elix-mappop__type">' + (place.t === 'bar' ? 'Cocktail bar' : 'Distillery') + '</p>' +
			'<p class="elix-mappop__name">' + escapeHTML(place.n) + '</p>' +
			(place.p ? '<p class="elix-mappop__place">' + escapeHTML(place.p) + '</p>' : '') +
			(tags ? '<ul class="elix-mappop__tags">' + tags + '</ul>' : '') +
			(place.perk ? '<p class="elix-mappop__perk">Member perk: ' + escapeHTML(place.perk) + '</p>' : '') +
			'<a class="elix-mappop__link" href="' + escapeHTML(place.u) + '">See details</a>' +
			'</div>';
	}

	function initMap(section) {
		var holder = section.querySelector('[data-elix-map]');
		var dataEl = section.querySelector('[data-elix-map-data]');
		if (!holder || !dataEl || holder.dataset.ready) return;
		holder.dataset.ready = '1';

		var places = [];
		try { places = JSON.parse(dataEl.textContent); } catch (e) { return; }
		var count = section.querySelector('[data-elix-map-count]');
		var filtersEl = section.querySelector('[data-elix-map-filters]');
		var listEl = section.querySelector('[data-elix-map-list]');
		var emptyEl = section.querySelector('[data-elix-map-empty]');
		var type = 'all';
		var features = [];

		function passes(place) {
			var keys = place.k || [];
			return (type === 'all' || place.t === type) &&
				features.every(function (f) { return keys.indexOf(f) > -1; });
		}

		function cardHTML(place, i) {
			var tags = (place.f || []).filter(Boolean).slice(0, 4).map(function (label) {
				return '<li>' + escapeHTML(label) + '</li>';
			}).join('');
			var img = place.i
				? '<img src="' + escapeHTML(place.i) + '" alt="" loading="lazy" width="96" height="96">'
				: '<span aria-hidden="true">' + escapeHTML((place.n || '?').charAt(0)) + '</span>';
			return '<li class="elix-maplist__item" data-i="' + i + '">' +
				'<a class="elix-maplist__card" href="' + escapeHTML(place.u) + '">' +
				'<span class="elix-maplist__img elix-maplist__img--' + place.t + '">' + img + '</span>' +
				'<span class="elix-maplist__body">' +
				'<span class="elix-maplist__type">' + (place.t === 'bar' ? 'Cocktail bar' : 'Distillery') + '</span>' +
				'<span class="elix-maplist__name">' + escapeHTML(place.n) + '</span>' +
				(place.p ? '<span class="elix-maplist__place">' + escapeHTML(place.p) + '</span>' : '') +
				(tags ? '<ul class="elix-maplist__tags">' + tags + '</ul>' : '') +
				(place.perk ? '<span class="elix-maplist__perk">Member perk</span>' : '') +
				'</span></a></li>';
		}

		function start() {
			loadMapbox().then(function (mapboxgl) {
				mapboxgl.accessToken = holder.dataset.token;
				var map = new mapboxgl.Map({
					container: holder,
					style: 'mapbox://styles/mapbox/dark-v11',
					center: [134, -28],
					zoom: 3.3,
					cooperativeGestures: true
				});
				map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

				function cardFor(i) {
					return listEl ? listEl.querySelector('[data-i="' + i + '"]') : null;
				}

				var markers = places.map(function (place, i) {
					var pin = document.createElement('button');
					pin.type = 'button';
					pin.className = 'elix-pin elix-pin--' + place.t + (place.perk ? ' has-perk' : '');
					pin.setAttribute('aria-label', place.n);
					// Mapbox positions the pin with its own transform, so the teardrop shape lives on an inner span
					pin.innerHTML = '<span class="elix-pin__dot"></span>';
					// Pin hover lights up its card; a tap also scrolls the list to it
					pin.addEventListener('mouseenter', function () {
						var card = cardFor(i);
						if (card) card.classList.add('is-hot');
					});
					pin.addEventListener('mouseleave', function () {
						var card = cardFor(i);
						if (card) card.classList.remove('is-hot');
					});
					pin.addEventListener('click', function () {
						var card = cardFor(i);
						if (!card || !listEl) return;
						listEl.querySelectorAll('.is-picked').forEach(function (c) { c.classList.remove('is-picked'); });
						card.classList.add('is-picked');
						listEl.scrollTo({ top: card.offsetTop - listEl.offsetTop - 8, behavior: 'smooth' });
					});
					var marker = new mapboxgl.Marker({ element: pin, anchor: 'bottom' })
						.setLngLat([place.lng, place.lat])
						.setPopup(new mapboxgl.Popup({ offset: 24, maxWidth: '280px' }).setHTML(popupHTML(place)))
						.addTo(map);
					return { place: place, marker: marker, pin: pin };
				});

				// The list shows what's inside the current view, nearest the centre first
				function renderList() {
					var inFilter = markers.filter(function (m) { return passes(m.place); });
					if (!listEl) {
						if (count) count.textContent = inFilter.length + ' of ' + markers.length + ' places';
						return;
					}
					var view = map.getBounds();
					var centre = map.getCenter();
					var inView = inFilter.filter(function (m) {
						return view.contains([m.place.lng, m.place.lat]);
					}).sort(function (a, b) {
						return km(centre.lat, centre.lng, a.place.lat, a.place.lng) - km(centre.lat, centre.lng, b.place.lat, b.place.lng);
					});
					listEl.innerHTML = inView.map(function (m) { return cardHTML(m.place, markers.indexOf(m)); }).join('');
					if (emptyEl) emptyEl.hidden = inView.length > 0;
					if (count) {
						count.textContent = inView.length === inFilter.length
							? inView.length + (inView.length === 1 ? ' place' : ' places')
							: inView.length + ' of ' + inFilter.length + ' places in this area';
					}
				}

				function apply(fit) {
					var bounds = new mapboxgl.LngLatBounds();
					var shown = 0;
					markers.forEach(function (m) {
						var ok = passes(m.place);
						m.marker.getElement().hidden = !ok;
						if (!ok) m.marker.getPopup().remove();
						if (ok) {
							shown += 1;
							bounds.extend([m.place.lng, m.place.lat]);
						}
					});
					if (fit && shown > 0) map.fitBounds(bounds, { padding: 60, maxZoom: 12, duration: 600 });
					renderList();
				}

				map.on('moveend', renderList);

				// Card hover lifts its pin
				if (listEl) {
					listEl.addEventListener('mouseover', function (event) {
						var item = event.target.closest('[data-i]');
						markers.forEach(function (m, i) {
							m.pin.classList.toggle('is-hot', !!item && String(i) === item.dataset.i);
						});
					});
					listEl.addEventListener('mouseleave', function () {
						markers.forEach(function (m) { m.pin.classList.remove('is-hot'); });
					});
				}

				if (filtersEl) {
					filtersEl.addEventListener('click', function (event) {
						var button = event.target.closest('button');
						if (!button) return;
						if (button.dataset.type) {
							type = button.dataset.type;
							filtersEl.querySelectorAll('[data-type]').forEach(function (b) {
								var on = b === button;
								b.classList.toggle('is-active', on);
								b.setAttribute('aria-pressed', on ? 'true' : 'false');
							});
						} else if (button.dataset.feature) {
							var key = button.dataset.feature;
							var on = features.indexOf(key) === -1;
							features = features.filter(function (f) { return f !== key; });
							if (on) features.push(key);
							button.classList.toggle('is-active', on);
							button.setAttribute('aria-pressed', on ? 'true' : 'false');
						}
						apply(true);
					});
				}

				map.on('load', function () { apply(true); });
			}).catch(function () {
				holder.innerHTML = '<p class="elix-past__empty">The map couldn’t load. Please try again later.</p>';
			});
		}

		// Wait until the map is nearly on screen before downloading Mapbox
		if ('IntersectionObserver' in window) {
			var io = new IntersectionObserver(function (entries) {
				if (entries.some(function (e) { return e.isIntersecting; })) {
					io.disconnect();
					start();
				}
			}, { rootMargin: '300px' });
			io.observe(holder);
		} else {
			start();
		}
	}

	/* ---------- Partner listing form (sections/elix-partner-form) ---------- */

	function initPartnerForm(root) {
		var form = root.querySelector('form');
		if (!form) return;

		// Show the distillery or bar fields, and disable the other set so it isn't submitted
		function setKind(kind) {
			root.querySelectorAll('[data-for]').forEach(function (el) {
				var on = el.getAttribute('data-for') === kind;
				el.hidden = !on;
				el.querySelectorAll('input, select, textarea').forEach(function (field) { field.disabled = !on; });
				if (/^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) el.disabled = !on;
			});
		}
		root.addEventListener('change', function (event) {
			if (event.target.matches('[data-elix-kind]')) setKind(event.target.getAttribute('data-elix-kind'));
		});

		// "Update your listing" links pre-fill: ?type=bar&listing=handle&name=Name
		var params = new URLSearchParams(window.location.search);
		var kind = params.get('type') === 'bar' ? 'bar' : 'distillery';
		var radio = root.querySelector('[data-elix-kind="' + kind + '"]');
		if (radio) radio.checked = true;
		setKind(kind);
		var listing = root.querySelector('[data-elix-listing]');
		if (listing && params.get('listing')) listing.value = params.get('listing');
		var nameField = root.querySelector('[data-elix-venue-name]');
		if (nameField && params.get('name') && !nameField.value) nameField.value = params.get('name');

		// Also save the answers to Klaviyo as an event, ready for bulk import (no marketing consent, nothing is sent)
		var key = root.getAttribute('data-klaviyo');
		form.addEventListener('submit', function () {
			if (!key) return;
			var props = {};
			var email = '';
			new FormData(form).forEach(function (value, name) {
				var match = /^contact\[(.+)\]$/.exec(name);
				if (!match || value === '') return;
				var field = match[1] === 'body' ? 'Notes' : match[1];
				if (field === 'email') email = value;
				props[field] = value;
			});
			if (!email) return;
			try {
				fetch('https://a.klaviyo.com/client/events?company_id=' + encodeURIComponent(key), {
					method: 'POST',
					keepalive: true,
					headers: { 'content-type': 'application/vnd.api+json', revision: '2024-10-15' },
					body: JSON.stringify({
						data: {
							type: 'event',
							attributes: {
								properties: props,
								metric: { data: { type: 'metric', attributes: { name: 'Partner listing submitted' } } },
								profile: { data: { type: 'profile', attributes: { email: email } } }
							}
						}
					})
				}).catch(function () { /* the emailed copy still arrives */ });
			} catch (e) { /* the emailed copy still arrives */ }
		});
	}

	/* ---------- Cocktail page: build it local ---------- */

	function initBuild(section) {
		var root = section.querySelector('[data-elix-build]');
		if (!root) return;
		root.hidden = false;

		var globalSelect = root.querySelector('[data-elix-build-scope]');
		var nearPanel = root.querySelector('[data-elix-near]');
		var radius = root.querySelector('[data-elix-radius]');
		var title = section.querySelector('[data-elix-build-title]');
		var cocktailName = section.dataset.cocktailName || '';
		var slots = section.querySelectorAll('[data-elix-slot]');

		slots.forEach(function (slot) {
			var control = slot.querySelector('[data-elix-slot-control]');
			if (control) control.hidden = false;
		});

		function apply() {
			var globalScope = readScope(globalSelect, radius);
			var mixed = false;
			var anyNear = globalScope.kind === 'near';

			slots.forEach(function (slot) {
				var select = slot.querySelector('[data-elix-slot-scope]');
				var scope = globalScope;
				if (select && select.value !== 'inherit') {
					mixed = true;
					scope = readScope(select, radius);
					if (scope.kind === 'near') anyNear = true;
				}

				var cards = slot.querySelectorAll('.elix-card');
				var shown = 0;
				cards.forEach(function (card) {
					var ok = cardPasses(card, scope);
					card.hidden = !ok;
					showDistance(card, scope);
					if (ok) shown += 1;
				});

				var empty = slot.querySelector('[data-elix-slot-filtered-empty]');
				if (empty) empty.hidden = !(cards.length > 0 && shown === 0);
			});

			if (nearPanel) nearPanel.hidden = !anyNear;

			if (title) {
				var text = '';
				if (mixed) text = 'Your ' + cocktailName + ' build';
				else if (globalScope.kind === 'region') text = globalScope.demonym + ' ' + cocktailName;
				else if (globalScope.kind === 'near') {
					text = globalScope.near
						? cocktailName + ' within ' + globalScope.radius + ' km'
						: 'Set your location to find bottles near you';
				}
				title.textContent = text;
				title.hidden = !text;
			}
		}

		root.addEventListener('change', apply);
		section.addEventListener('change', function (event) {
			if (event.target.matches('[data-elix-slot-scope]')) apply();
		});
		if (radius) radius.addEventListener('input', apply);
		document.addEventListener('elix:location', apply);
		apply();
	}

	/* ---------- List pages: finder, distance sort, what can I make ---------- */

	function initFinder(section) {
		var root = section.querySelector('[data-elix-finder]');
		var list = section.querySelector('[data-elix-list]');
		if (!root || !list) return;
		root.hidden = false;

		var cards = Array.prototype.slice.call(list.querySelectorAll('.elix-card'));
		cards.forEach(function (card, i) { card.dataset.order = i; });

		var search = root.querySelector('[data-elix-search]');
		var filters = root.querySelectorAll('[data-elix-filter]');
		var scopeSelect = root.querySelector('[data-elix-scope]');
		var radius = root.querySelector('[data-elix-radius]');
		var nearPanel = root.querySelector('[data-elix-near]');
		var canMakeToggle = root.querySelector('[data-elix-can-make]');
		var count = root.querySelector('[data-elix-count]');
		var empty = section.querySelector('[data-elix-filtered-empty]');
		var typeFilter = root.querySelector('[data-elix-filter="type"]');
		var categoryFilter = root.querySelector('[data-elix-filter="category"]');

		// Only offer categories of the chosen spirit type
		function narrowCategories() {
			if (!typeFilter || !categoryFilter) return;
			Array.prototype.forEach.call(categoryFilter.options, function (option) {
				var fits = !option.value || !typeFilter.value || option.dataset.type === typeFilter.value;
				option.hidden = !fits;
				option.disabled = !fits;
				if (!fits && option.selected) categoryFilter.value = '';
			});
		}
		if (typeFilter) typeFilter.addEventListener('change', narrowCategories);

		function apply() {
			var query = search ? search.value.trim().toLowerCase() : '';
			var scope = readScope(scopeSelect, radius);
			if (nearPanel) nearPanel.hidden = scope.kind !== 'near';

			var shown = 0;
			cards.forEach(function (card) {
				var ok = !query || (card.dataset.search || '').indexOf(query) > -1;
				filters.forEach(function (filter) {
					if (ok && filter.value) ok = card.dataset[filter.dataset.elixFilter] === filter.value;
				});
				if (ok) ok = cardPasses(card, scope);
				if (ok && canMakeToggle && canMakeToggle.checked) ok = card.dataset.canMake === 'yes';
				card.hidden = !ok;
				showDistance(card, scope);
				if (ok) shown += 1;
			});

			// Nearest first when searching by distance, otherwise the original order
			var sorted = cards.slice().sort(function (a, b) {
				if (scope.kind === 'near' && scope.near) {
					var da = cardDistance(a, scope.near);
					var db = cardDistance(b, scope.near);
					return (da === null ? Infinity : da) - (db === null ? Infinity : db);
				}
				return a.dataset.order - b.dataset.order;
			});
			sorted.forEach(function (card) { list.appendChild(card); });

			if (count) count.textContent = shown + ' of ' + cards.length;
			if (empty) empty.hidden = shown > 0;
		}

		root.addEventListener('input', apply);
		root.addEventListener('change', apply);
		document.addEventListener('elix:location', apply);
		section.addEventListener('elix:can-make', apply);
		apply();
	}

	// The bar holds bottle handles plus "type:Gin" entries for ticked types.
	var TYPE_PREFIX = 'type:';

	function isType(entry) {
		return entry.indexOf(TYPE_PREFIX) === 0;
	}

	// Owned bottles from the index, plus a stand-in for each ticked type.
	function ownedSpirits(index, bar) {
		var owned = index.spirits.filter(function (s) { return bar.indexOf(s.h) > -1; });
		bar.filter(isType).forEach(function (entry) {
			owned.push({ generic: true, type: entry.slice(TYPE_PREFIX.length), tags: [] });
		});
		return owned;
	}

	// Cocktail list: tick the spirit types you have, search for your exact
	// bottles, and see which drinks you can make
	function initBarPanel(section) {
		var panel = section.querySelector('[data-elix-bar-panel]');
		var list = section.querySelector('[data-elix-list]');
		if (!panel || !list) return;
		panel.hidden = false;

		var typeBoxes = panel.querySelectorAll('.elix-bar__types input[type="checkbox"]');
		var search = panel.querySelector('[data-elix-bar-q]');
		var results = panel.querySelector('[data-elix-bar-results]');
		var scope = panel.querySelector('[data-elix-bar-scope]');
		var mine = panel.querySelector('[data-elix-bar-mine]');
		var chips = panel.querySelector('[data-elix-bar-chips]');
		var countEl = panel.querySelector('[data-elix-bar-count]');
		var canMakeToggle = section.querySelector('[data-elix-can-make]');
		var labels = {
			can: panel.dataset.canText || 'You can make this',
			missing: panel.dataset.missingText || 'Missing:'
		};
		var MAX_RESULTS = 20;
		var index = null;

		function el(tag, className, text) {
			var node = document.createElement(tag);
			if (className) node.className = className;
			if (text) node.textContent = text;
			return node;
		}

		function distilleryName(spirit) {
			var d = index && index.distilleries && index.distilleries[spirit.d];
			return d ? d.n : '';
		}

		function tickedTypes() {
			return getBar().filter(isType).map(function (entry) { return entry.slice(TYPE_PREFIX.length); });
		}

		function renderChips() {
			var bottles = getBar().filter(function (entry) { return !isType(entry); });
			chips.innerHTML = '';
			bottles.forEach(function (handle) {
				var spirit = index && index.spirits.filter(function (s) { return s.h === handle; })[0];
				var chip = el('li', 'elix-bar__chip');
				chip.appendChild(el('span', '', spirit ? spirit.n : handle.replace(/-/g, ' ')));
				if (spirit && distilleryName(spirit)) chip.appendChild(el('small', '', distilleryName(spirit)));
				var remove = el('button', 'elix-bar__remove', '×');
				remove.type = 'button';
				remove.setAttribute('aria-label', 'Remove ' + (spirit ? spirit.n : handle));
				remove.addEventListener('click', function () { toggleInBar(handle, false); });
				chip.appendChild(remove);
				chips.appendChild(chip);
			});
			mine.hidden = !bottles.length;
		}

		function renderResults() {
			results.innerHTML = '';
			var words = search.value.toLowerCase().split(/\s+/).filter(Boolean);
			if (!index || !words.length || search.value.trim().length < 2) return;
			var types = tickedTypes();
			var bar = getBar();
			var hits = index.spirits.filter(function (s) {
				if (bar.indexOf(s.h) > -1) return false;
				if (types.length && types.indexOf(s.type) < 0) return false;
				var text = (s.n + ' ' + distilleryName(s)).toLowerCase();
				return words.every(function (w) { return text.indexOf(w) > -1; });
			});
			if (!hits.length) {
				results.appendChild(el('li', 'elix-bar__none', types.length
					? 'No ' + types.join(' or ').toLowerCase() + ' matches that. Untick the types to search everything.'
					: 'No bottle matches that yet.'));
				return;
			}
			hits.slice(0, MAX_RESULTS).forEach(function (s) {
				var item = el('li');
				var button = el('button', 'elix-bar__result');
				button.type = 'button';
				button.appendChild(el('span', '', s.n));
				button.appendChild(el('small', '', [distilleryName(s), s.type].filter(Boolean).join(' · ')));
				button.addEventListener('click', function () {
					toggleInBar(s.h, true);
					search.value = '';
					results.innerHTML = '';
					search.focus();
				});
				item.appendChild(button);
				results.appendChild(item);
			});
			if (hits.length > MAX_RESULTS) {
				results.appendChild(el('li', 'elix-bar__none', (hits.length - MAX_RESULTS) + ' more: keep typing to narrow it down.'));
			}
		}

		function syncBoxes() {
			var bar = getBar();
			typeBoxes.forEach(function (box) { box.checked = bar.indexOf(box.value) > -1; });
			if (countEl) countEl.textContent = bar.length ? '(' + bar.length + ')' : '';
			var types = tickedTypes();
			if (scope) scope.textContent = types.length ? 'Find your ' + types.join(', ').toLowerCase() : 'Find your bottles';
			renderChips();
			renderResults();
		}

		var timer;
		search.addEventListener('input', function () {
			clearTimeout(timer);
			timer = setTimeout(renderResults, 150);
		});
		search.addEventListener('keydown', function (event) {
			if (event.key === 'Enter') {
				event.preventDefault();
				var first = results.querySelector('.elix-bar__result');
				if (first) first.click();
			}
		});

		loadIndex()
			.then(function (data) {
				index = data;
				syncBoxes();
			})
			.catch(function () {
				search.disabled = true;
				search.placeholder = 'Search is unavailable right now';
			});

		function updateCards() {
			var bar = getBar();
			if (!bar.length) {
				list.querySelectorAll('.elix-card[data-cocktail]').forEach(function (card) {
					delete card.dataset.canMake;
					var status = card.querySelector('[data-elix-make]');
					if (status) status.hidden = true;
				});
				if (canMakeToggle) canMakeToggle.disabled = true;
				section.dispatchEvent(new CustomEvent('elix:can-make'));
				return;
			}

			loadIndex()
				.then(function (index) {
					var owned = ownedSpirits(index, bar);
					var byHandle = {};
					index.cocktails.forEach(function (c) { byHandle[c.h] = c; });

					list.querySelectorAll('.elix-card[data-cocktail]').forEach(function (card) {
						var cocktail = byHandle[card.dataset.cocktail];
						var status = card.querySelector('[data-elix-make]');
						if (!cocktail || !status) return;
						var result = canMake(cocktail, owned);
						card.dataset.canMake = result.ok ? 'yes' : 'no';
						status.hidden = false;
						status.classList.toggle('is-ok', result.ok);
						status.textContent = result.ok
							? labels.can
							: labels.missing + ' ' + result.missing.join(', ').toLowerCase();
					});
					if (canMakeToggle) canMakeToggle.disabled = false;
					section.dispatchEvent(new CustomEvent('elix:can-make'));
				})
				.catch(function () {
					/* Index unavailable: leave cards as they are */
				});
		}

		panel.addEventListener('change', function (event) {
			if (event.target.matches('.elix-bar__types input[type="checkbox"]')) {
				toggleInBar(event.target.value, event.target.checked);
			}
		});

		document.addEventListener('elix:bar', function () {
			syncBoxes();
			updateCards();
		});
		syncBoxes();
		updateCards();
	}

	/* ---------- This month's box (sections/subscriptions-kit) ---------- */

	function money(cents) {
		var dollars = cents / 100;
		return '$' + dollars.toLocaleString('en-AU', {
			minimumFractionDigits: dollars % 1 ? 2 : 0,
			maximumFractionDigits: 2
		});
	}

	// Today as YYYYMMDD in the visitor's time, to compare with each box's data-starts
	function todayNumber() {
		var d = new Date();
		return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
	}

	function initMonthlyBox(section) {
		var tabs = Array.prototype.slice.call(section.querySelectorAll('[data-elix-box-tab]'));
		var panels = Array.prototype.slice.call(section.querySelectorAll('[data-elix-box-panel]'));
		var empty = section.querySelector('[data-elix-box-empty]');
		var today = todayNumber();
		var current = {};

		// Each plan shows its latest box that has started; the page may be cached from before a switch date
		panels.forEach(function (panel) {
			var plan = panel.getAttribute('data-plan');
			var starts = Number(panel.getAttribute('data-starts'));
			var best = current[plan];
			if (starts <= today && (!best || starts > Number(best.getAttribute('data-starts')))) current[plan] = panel;
		});

		var shown = tabs.filter(function (tab) {
			tab.hidden = !current[tab.getAttribute('data-plan')];
			return !tab.hidden;
		});
		if (tabs.length) tabs[0].parentNode.hidden = shown.length < 2;
		if (empty) empty.hidden = shown.length > 0;

		function show(plan) {
			tabs.forEach(function (tab) {
				var on = tab.getAttribute('data-plan') === plan;
				tab.setAttribute('aria-selected', on ? 'true' : 'false');
				tab.tabIndex = on ? 0 : -1;
			});
			panels.forEach(function (panel) {
				panel.hidden = panel !== current[plan];
			});
		}

		function retotal(panel) {
			var choice = panel.querySelector('[data-elix-kit]:checked');
			var kit = choice ? Number(choice.getAttribute('data-cents')) : 0;
			var total = Number(panel.getAttribute('data-box-cents')) + kit;
			var save = total - Number(panel.getAttribute('data-pay-cents'));
			var kitValue = panel.querySelector('[data-elix-kit-value]');
			var totalValue = panel.querySelector('[data-elix-total]');
			var saveLine = panel.querySelector('[data-elix-save]');
			if (kitValue) kitValue.textContent = '+' + money(kit);
			if (totalValue) totalValue.textContent = money(total);
			if (saveLine) {
				saveLine.hidden = save <= 0;
				saveLine.querySelector('[data-elix-save-amount]').textContent = money(save);
			}
		}

		tabs.forEach(function (tab) {
			tab.addEventListener('click', function () {
				show(tab.getAttribute('data-plan'));
			});
			tab.addEventListener('keydown', function (event) {
				if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
				var at = shown.indexOf(tab);
				var next = shown[(at + (event.key === 'ArrowRight' ? 1 : shown.length - 1)) % shown.length];
				show(next.getAttribute('data-plan'));
				next.focus();
			});
		});

		// The same welcome kit stays picked when switching plans
		section.addEventListener('change', function (event) {
			if (!event.target.matches('[data-elix-kit]')) return;
			var index = event.target.getAttribute('data-index');
			section.querySelectorAll('[data-elix-kit][data-index="' + index + '"]').forEach(function (input) {
				input.checked = true;
			});
			panels.forEach(retotal);
		});

		// Gallery: a thumbnail swaps the big photo above it
		section.addEventListener('click', function (event) {
			var thumb = event.target.closest('[data-elix-shot-thumb]');
			if (!thumb) return;
			var gallery = thumb.closest('[data-elix-gallery]');
			var index = thumb.getAttribute('data-elix-shot-thumb');
			gallery.querySelectorAll('[data-elix-shot]').forEach(function (shot) {
				shot.hidden = shot.getAttribute('data-elix-shot') !== index;
			});
			gallery.querySelectorAll('[data-elix-shot-thumb]').forEach(function (button) {
				button.setAttribute('aria-pressed', button === thumb ? 'true' : 'false');
			});
		});

		panels.forEach(retotal);
		var selected = shown.filter(function (tab) {
			return tab.getAttribute('aria-selected') === 'true';
		})[0] || shown[0];
		if (selected) show(selected.getAttribute('data-plan'));
	}

	/* ---------- Boot ---------- */

	function init() {
		document.querySelectorAll('[data-elix-box]').forEach(initMonthlyBox);
		document.querySelectorAll('[data-elix-location]').forEach(initLocationControls);
		document.querySelectorAll('[data-elix-bar-toggle]').forEach(initBarToggle);
		document.querySelectorAll('[data-elix-save]').forEach(initSaveToggle);
		document.querySelectorAll('[data-elix-saved]').forEach(initSavedList);
		document.querySelectorAll('[data-elix-member-card]').forEach(initMemberCard);
		document.querySelectorAll('.elix-mapsec').forEach(initMap);
		document.querySelectorAll('[data-elix-partner-form]').forEach(initPartnerForm);
		document.querySelectorAll('.elix-bottles').forEach(initBuild);
		document.querySelectorAll('.elix-library-list').forEach(function (section) {
			initBarPanel(section);
			initFinder(section);
		});
		markOwnedCards();
		document.addEventListener('elix:bar', markOwnedCards);

		// Theme editor re-renders a section in place; wire up a fresh box widget
		document.addEventListener('shopify:section:load', function (event) {
			event.target.querySelectorAll('[data-elix-box]').forEach(initMonthlyBox);
			if (event.target.querySelector('.elix-mapsec')) event.target.querySelectorAll('.elix-mapsec').forEach(initMap);
		});
	}

	window.ElixLibrary = {
		initMonthlyBox: initMonthlyBox,
		loadIndex: loadIndex,
		score: score,
		canMake: canMake,
		km: km,
		getBar: getBar,
		setBar: setBar,
		getSaved: getSaved,
		setSaved: setSaved
	};

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
	else init();
})();
